import { query } from "./strapi";

/**
 * Validates if a cart is active and belongs to the current session
 * The sessionId cookie is automatically included in the request
 */
export async function validateCart(cartId: string) {
  try {
    if (!cartId) {
      console.error("❌ cartId is required for validation");
      return { isValid: false, message: "No cartId provided" };
    }

    // Send cartId in header instead of query string
    const response = await query(`cart/validate`, {
      method: "GET",
      headers: {
        "x-cart-id": cartId,
      },
      credentials: "include", // Important: Include cookies for sessionId
    });

    console.log("✅ Cart validation result:", response);

    // If cart is valid, store its ID in localStorage
    if (response?.isValid && response?.cartId) {
      localStorage.setItem("cartId", response.cartId);
    }

    return response;
  } catch (error) {
    console.error("❌ Error validating cart:", error);
    return { isValid: false, message: "Error during validation" };
  }
}

/**
 * Find or Create Cart
 * The sessionId cookie is automatically included in the request
 */
export async function findOrCreateCart() {
  try {
    console.log("🔍 Finding or creating cart");

    const response = await query(`cart/find-or-create`, {
      method: "POST", // Changed from GET to POST to follow REST principles
      credentials: "include", // Include cookies for sessionId
    });

    if (!response?.cartId) {
      throw new Error("❌ Failed to find or create cart");
    }

    // Store cartId for future use
    localStorage.setItem("cartId", response.cartId);

    console.log("✅ Cart found/created:", response.cartId);

    return {
      cartId: response.cartId,
      cartVersion: response.cartVersion || 1,
      cartStatus: "active",
      totalPrice: response.totals?.originalSubtotal || 0,
      totalDeliveryFee: response.totals?.deliveryFeeTotal || 0,
      finalTotal: response.totals?.grandTotal || 0,
      promotionalDiscountTotal: response.totals?.promotionalDiscountTotal || 0,
      couponDiscountTotal: response.totals?.couponDiscountTotal || 0,
      appliedCoupons: response.appliedCoupons || [],
      groups: response.groups || [],
      cleanPricing: response, // Phase 3.2: Clean structure is now at root level
    };
  } catch (error) {
    console.error("❌ Error in findOrCreateCart:", error);
    throw error;
  }
}

export async function validateCartStock(cartId: string) {
  if (!cartId) {
    throw new Error("cartId is required");
  }

  try {
    console.log(`🔍 Validating stock for cart: ${cartId}`);

    const response = await query(`cart/validate-stock`, {
      method: "GET",
      headers: {
        "x-cart-id": cartId, // Pass cartId in header instead of query string
      },
      credentials: "include", // Include cookies for sessionId
    });

    console.log(
      `✅ Stock validation complete: ${response?.isValid ? "Valid" : "Invalid"}`
    );
    console.log("🔁 Cart details validateCartStock:", response);

    // Phase 3.2: validateCartStock only returns validation data, not cart data
    // Backend response structure: { isValid, validationResults }
    return {
      isValid: response?.isValid,
      validationResults: response?.validationResults || [],
    };
  } catch (error) {
    console.error("❌ Error validating cart stock:", error);
    throw error;
  }
}

/**
 * Update cart properties
 * Requires both sessionId (via cookies) and cartId (via header)
 */
export async function updateCart(updates: Record<string, any>) {
  const cartId = localStorage.getItem("cartId");
  if (!cartId) {
    throw new Error("No cart ID found in localStorage");
  }

  try {
    console.log("🔄 Updating cart:", updates);

    const response = await query(`cart/update`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "x-cart-id": cartId,
      },
      credentials: "include", // Include cookies for sessionId
      body: JSON.stringify(updates),
    });

    console.log("✅ Cart updated successfully");
    console.log("Response updateCart:", JSON.stringify(response));

    // Phase 3.2: Return clean structure response properly formatted
    return {
      cartId: response.cartId,
      cartVersion: response.cartVersion,
      groups: response.groups || [],
      totals: response.totals,
      appliedCoupons: response.appliedCoupons || [],
      appliedPromotions: response.appliedPromotions || [],
      cleanPricing: response, // Phase 3.2: Clean structure is now at root level
    };
  } catch (error) {
    console.error("❌ Error updating cart:", error);
    throw error;
  }
}

/**
 * Clear the cart
 * Requires both sessionId (via cookies) and cartId (via header)
 */
export async function clearCart() {
  const cartId = localStorage.getItem("cartId");
  if (!cartId) {
    throw new Error("No cart ID found in localStorage");
  }

  try {
    console.log("🔄 Clearing cart");

    const response = await query(`cart/clear`, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        "x-cart-id": cartId,
      },
      credentials: "include", // Include cookies for sessionId
    });

    console.log("✅ Cart cleared successfully");

    // Phase 3.2: Return clean structure response for cleared cart
    return {
      cartId: response.cartId,
      cartVersion: response.cartVersion || 1,
      groups: response.groups || [],
      totals: response.totals || {
        originalSubtotal: 0,
        customizationTotal: 0,
        promotionalDiscountTotal: 0,
        couponDiscountTotal: 0,
        deliveryFeeTotal: 0,
        finalSubtotal: 0,
        grandTotal: 0,
      },
      appliedCoupons: response.appliedCoupons || [],
      appliedPromotions: response.appliedPromotions || [],
      cleanPricing: response, // Phase 3.2: Clean structure is now at root level
    };
  } catch (error) {
    console.error("❌ Error clearing cart:", error);
    throw error;
  }
}
